import "./index.scss";
import "../../locales/i18n";
import { useMutation } from "@apollo/client";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import Checkbox from "@/components/components/Checkbox";
import { SimpleDropdown } from "@/components/components/Dropdown";
import { TDropdownOption } from "@/components/components/Dropdown/dropdown.type";
import { MessageType } from "@/components/enums";
import FormBody from "@/messenger/components/FormBody";
import FormFooter from "@/messenger/components/FormFooter";
import FormHeader from "@/messenger/components/FormHeader";
import TextInput from "@/messenger/components/TextInput";
import { CHAT } from "@/messenger/constants/routing";
import { addNotification, removeNotification } from "@/messenger/ducks/general/notifications.duck";
import { deviceTypeSelector } from "@/messenger/ducks/selectors/general";
import { createContact } from "@/messenger/graphql/mutations";
import { getAllTopics, getPhoneType } from "@/messenger/services/data.service";
import { detectMobile } from "@/messenger/utils/deviceType";
import { addContactValidation, fetchNumbers } from "@/messenger/utils/helpers";
import { Country, Locale, SuffixOptions } from "@/messenger/constants/misc"
import Button from "@/components/components/Button";
import { ButtonColor, ButtonSize } from "@/components/components/Button/button.enum";
import { CountryStatesList } from "src/constants/countryStates";
import { setSearchValueAC } from "@/messenger/ducks/general/conversationRefinementsReducer.duck";
import { conversationRefinementsSelector, conversationsSelector } from "@/messenger/selectors";
import { contactsSelectors } from "@/messenger/ducks/entities/contacts";
import { TStore } from "@/messenger/types/store.types";
import { representativeSettingsSelectors } from "@/messenger/ducks/entities/representativeSettings";

interface TAddAContactProps {
    onClose: () => void;
    selectedOption?: TDropdownOption<string>;
    selectedOptions?: Array<TDropdownOption>;
}
interface TPhoneStatus {
    isDuplicate?: boolean;
    exisitingConversationId?: string;
    carrier?: string;
    type?: string;
}

declare global {
interface Crypto {
    randomUUID: () => string;
}
}

const AddAContact = ({ onClose, selectedOption }: TAddAContactProps) => {
    const state = useSelector((s: TStore) => s);
    const deviceType = useSelector(deviceTypeSelector);
    const isMobile = detectMobile(deviceType);
    const dispatch = useDispatch();

    const currentRepresentative = useSelector(representativeSettingsSelectors.selectAll);
    const currentRepresentativeId = currentRepresentative[0]?.participantId

    const searchValue = useSelector(conversationRefinementsSelector)?.search?.value;
    const hasQuery = searchValue?.length > 0;

    const height = "calc(100vh - 25px)";
    const hardWidth = isMobile ? "100%" : "379px";
    const componentStyles: React.CSSProperties = {
        position: "fixed",
        marginTop: 0,
        top: 25,
        right: 0,
        height,
        width: hardWidth,
        marginRight: 0,
    };
    const { t } = useTranslation();
    const [isUsEnv, setIsUsEnv] = useState<boolean>(true);
    
    const [hasConsent, setHasConsent] = useState(false);
    const [phoneStatus, setPhoneStatus] = useState<TPhoneStatus>({});
    const [twilioContext, setTwilioContext] = useState<any>({});
    const [topicOptions, setTopicOptions] = useState<[TDropdownOption]>(null);
    const [suffixSelection, setSuffixSelection] = useState<TDropdownOption>(null);

    const [topicSelection, setTopicSelection] = useState<TDropdownOption>(null);
    const [outboundNumberSelection, setOutboundNumberSelection] = useState<TDropdownOption>(null);
    const [localeSelection, setLocaleSelection] = useState<TDropdownOption>({ label: "en_US", heading: "English (US)" });
    const [countrySelection, setCountrySelection] = useState<TDropdownOption>({ label: "US", heading: "United States" });
    const [administrativeAreaSelection, setAdministrativeAreaSelection] = useState<TDropdownOption>();
    const [advancedOption, setAdvancedOption] = useState<boolean>(false);
    const [numbers, setNumbers] = useState([]);
    const [contactPhone, setContactPhone] = useState<string>(null);
    

    
    const renderPhoneMessage = () => {
        if (phoneStatus?.exisitingConversationId) {
            return (
                <span>
                    {t("You already have a conversation with this contact.")} <a href={`/${CHAT}/${phoneStatus?.exisitingConversationId}`}>{t("Open it")}</a></span>
            );
        }
        if (phoneStatus?.isDuplicate) {

            return (
                <span>{t("Warning! There are other conversations with this contact.")} <a href="#" onClick={() => handleSearchContacts()}>{t("Search for them")}</a></span>
            );
        }
        if (twilioContext?.type === "landline") {
            return (
                <span>{t("This number does't accept SMS messages.")}</span>
            );
        } else if (twilioContext?.type !== "mobile") {
            return (
                <span>{t("This number is a VOIP line and might receive SMS messages.")}</span>
            );
        }
    };

    useEffect(() => {
        if (window.location.href.includes("us.textkit.io")) {
            setIsUsEnv(true)
            console.log(isUsEnv)
        }
        // Get outbound numbers too
        (!numbers || numbers === undefined || numbers?.length < 1) && fetchOutboundNumbers();
        (!topicOptions || topicOptions === undefined || topicOptions?.length < 1) && getTopics();
    }, []);
    
    useEffect(() => {
        const lastSelectedTopic = JSON.parse(localStorage.getItem('LAST_SELECTED_TOPIC'));

        if (topicOptions) {
            topicOptions.map(topic => {
                if(topic?.id === lastSelectedTopic?.id) {
                    setTopicSelection(lastSelectedTopic);
                }
            });
        }
    }, [topicOptions]);
    
    useEffect(() => {
        const lastSelectedOutboundNumber = JSON.parse(localStorage.getItem('LAST_SELECTED_OUTBOUND_NUMBER'));
    
        if (numbers && lastSelectedOutboundNumber) {
            numbers.map(number => {
                if(number.heading === lastSelectedOutboundNumber?.heading) {
                    setOutboundNumberSelection(lastSelectedOutboundNumber);
                }
            });
        }
    }, [numbers]);

    const fetchOutboundNumbers = async (): Promise<any> => {

        try {
            if (numbers.length === 0) {
                const numbers: { outbound_address: string }[] = await fetchNumbers();
                if (numbers && numbers?.length > 0) {
                    setNumbers(numbers);
                    setOutboundNumberSelection({
                        label: numbers[0]?.outbound_address,
                        heading: numbers[0]?.outbound_address
                    });
                }
            }

        } catch (error) {
            console.log(error);
            return [];
        }

    };

    const numbersOptions = numbers && numbers?.filter(num => {
        return Boolean(num.outbound_address);
    }).map(item => ({
        label: item?.outbound_address,
        ariaLabel: item?.outbound_address,
        heading: item?.outbound_address,
    }));

    const suffixOptions = Object.values(SuffixOptions).map(el => {
        return ({
            label: el,
            ariaLabel: el,
            heading: el,
        });
    });

    const localeOptions = Locale.map(el => {
        return ({
            label: el?.id,
            ariaLabel: el?.name,
            heading: el?.name,
        });
    });

    const countryOptions = Country.map(el => {
        return ({
            label: el?.id,
            ariaLabel: el?.name,
            heading: el?.name,
        });
    });

    const administrativeAreaOptions = CountryStatesList && CountryStatesList.map((el: { name: string, abbreviation: string, states: [{ name: string, abbreviation: string }] }) => {
        if (el?.abbreviation === countrySelection?.label) {
            return el?.states.map(state => {
                return ({
                    label: state?.abbreviation,
                    ariaLabel: state?.name,
                    heading: state?.name,
                })
            })
        }
    })?.filter(ele => Boolean(ele))[0];

    const getTopics = async () => {
        const { data } = await getAllTopics();
        
        if (data?.topics && !topicOptions) {
            const unsortedTopics = data?.topics;

            if (unsortedTopics) {
                const sortedTopics = unsortedTopics?.map(item => {
                    if (item) {
                        return ({
                            id: item?.id,
                            label: item?.name,
                            ariaLabel: item?.name,
                            heading: item?.name,
                        });
                     }
                })
                .sort((c1, c2) => c1?.label?.localeCompare(c2.label));

                setTopicOptions(sortedTopics);
            }
        }
    };


    const [createContactMutation] = useMutation(createContact);

    const { values, errors, handleChange, handleBlur, handleSubmit, setFieldValue, dirty, isValid, resetForm, setFieldError } = useFormik({
        initialValues: {
            firstName: "",
            lastName: "",
            businessName: "",
            middleName: "",
            phoneNumber: "",
            externalId: "",
            streetNumber: '',
            streetName: '',
            streetSuffix: '',
            streetDirection: '',
            unitType: '',
            unitNumber: '',
            locality: '',
            postalCode: '',
            poBox: '',
            ruralRoute: '',
            locator: '',
        },
        validationSchema: addContactValidation,
        onSubmit: () => {
            if (isValid && hasConsent) {
                const contactId = crypto.randomUUID();
                const twilioParticipantId = crypto.randomUUID();
                const twilioParticipantIdForRep = crypto.randomUUID();
                const twilioConversationId = crypto.randomUUID();
                const outboundNumber = (outboundNumberSelection && outboundNumberSelection?.label) ? outboundNumberSelection?.label : numbersOptions[0]?.label;
                const contactlocale = localeSelection ? localeSelection?.label : 'en_US';

                createContactMutation({
                    variables: {
                        CreateContactInput: {
                            uuid: contactId,
                            family_name: values?.lastName,
                            middle_name: values?.middleName,
                            given_name: values?.firstName,
                            name_suffix: suffixSelection && suffixSelection?.label,
                            preferred_name: "",                            
                            locale: contactlocale,
                            name_business: values?.businessName,
                            external_id: values?.externalId,
                            street_num: values?.streetNumber,
                            street_name: values?.streetName,
                            street_suffix: values?.streetSuffix,
                            street_direction: values?.streetDirection,
                            unit_num: values?.unitNumber,
                            unit_type: values?.unitType,
                            rural_route: values?.ruralRoute,
                            po_box: values?.poBox,
                            postal_code: countrySelection?.heading !== "US" ? values?.postalCode : '',
                            zip_code:  countrySelection?.heading === "US" ? values?.postalCode : '',
                            locator: values?.locator,
                            locality: values?.locality,
                            administrative_area_1: administrativeAreaSelection?.heading,
                            administrative_area_2: "",
                            administrative_area_3: "",
                            administrative_area_4: values?.locality,
                            country_iso2: countrySelection?.heading,
                            metadata: {
                                externalId: values?.externalId,
                                phoneNumber: values?.phoneNumber,
                                topicId: topicSelection?.id,
                                topicHeading: topicSelection?.heading,
                                outboundNumber: outboundNumber,
                            },
                            conversation_topics: {
                                data: {
                                    parent_uuid: topicSelection?.id,
                                    context_text: "Conversation Topic",
                                    inbound_address: values?.phoneNumber,
                                    outbound_address: outboundNumber,
                                    channel: "SMS",
                                    locale: contactlocale,
                                }
                            },
                            linked: {
                                data: [{
                                    uuid: twilioParticipantId,
                                    inbound_address: values?.phoneNumber,
                                    outbound_address: outboundNumber,
                                    context_text: "Participant",
                                    locale: contactlocale,
                                    channel: "SMS",
                                    type: "MB",
                                    child_of: {
                                        data: {
                                            uuid: twilioConversationId,
                                            locale: contactlocale,
                                            type: "CH",
                                            state: "TO_CONTACT",
                                            channel: "SMS",
                                            linked_uuid: contactId,
                                            context_text: "Conversation",
                                            inbound_address: values?.phoneNumber,
                                            outbound_address: outboundNumber,
                                            parent_uuid: "49684668-7052-4d2d-886c-56b43aae252d",
                                            parent_service_uuid: "49684668-7052-4d2d-886c-56b43aae252d"
                                        }
                                    }       
                                },
                                {
                                    uuid: twilioParticipantIdForRep,
                                    inbound_address: values?.phoneNumber,
                                    outbound_address: outboundNumber,
                                    context_text: "Representative",
                                    channel: null,
                                    type: "MB",
                                    parent_uuid: twilioConversationId,                                    
                                }]
                            }
                        }
                    }
                });
                localStorage.setItem("LAST_SELECTED_OUTBOUND_NUMBER", JSON.stringify(outboundNumberSelection));
                localStorage.setItem("LAST_SELECTED_TOPIC", JSON.stringify(topicSelection));
            } 

            onClose();
            const notification = {
                id: `${values?.phoneNumber}`,
                type: MessageType.NEUTRAL,
                message: "Request submitted. The contact should appear shortly under To Contact.",
            };

            dispatch(addNotification(notification));
            setTimeout(() => dispatch(removeNotification(`${values?.phoneNumber}`)), 3000);
            
        },
    });

    const clearForm = () => {
        resetForm();
        setFieldValue("businessName", "");
        setFieldValue("firstName", "");
        setFieldValue("lastName", "");
        setFieldValue("middleName", "");
        setFieldValue("phoneNumber", "");
        setFieldValue("topicId", "");
        setFieldValue("externalId", "");
        setFieldValue("poBox", "");       
        setFieldValue("ruralRoute ", "");      
        setFieldError("businessName", undefined);
        setFieldError("middleName", undefined);
        setFieldError("firstName", undefined);
        setFieldError("lastName", undefined);
        setFieldError("phoneNumber", undefined);
        setFieldError("topicId", undefined);
        setFieldError("externalId", undefined);
    };

    const handleCancel = () => {
        clearForm();
        onClose();
    };

    const handleSearchContacts = () => {
        if (contactPhone && contactPhone !== null && !hasQuery) { 
            dispatch(setSearchValueAC(contactPhone)); 
        }

    };

    const handleSubmitForm = () => {
        handleSubmit();
    };

    const handlePhoneValue = (v) => {
        let newValue = v?.replace("-", "").replace(" ", "").replace("(", "").replace(")", "");
        if (newValue?.slice(0, 1) !== "+") newValue = "+1" + newValue;
        return newValue;
    };

    const checkUniquePhoneCombination = (phone: string) => {
        const contacts = contactsSelectors.selectAll(state);
        const convos = conversationsSelector(state);

        const existingContactsIdList = []
       
        contacts.forEach(contact => {
            if(contact?.phoneNumber?.number === phone) {
                existingContactsIdList.push(contact.id);
            }
        });

        const existingMyConversation = convos.find(convo => {
            if(existingContactsIdList.includes(convo.contact) && convo.representativeId === currentRepresentativeId && convo.representativePhone === outboundNumberSelection?.label) return convo;
        })
        
        const existingTeamConversation = convos.find(convo => {
            if(existingContactsIdList.includes(convo.contact) && convo.representativeId !== currentRepresentativeId && convo.representativePhone === outboundNumberSelection?.label) return convo;
        })

        if (existingMyConversation) {
            setPhoneStatus({ isDuplicate: true, exisitingConversationId: existingMyConversation?.id });
        } else if(existingTeamConversation) {
            setPhoneStatus({ isDuplicate: true, exisitingConversationId: null });
        } else {
            setPhoneStatus({ isDuplicate: false, exisitingConversationId: null });
        }
    }

    const onPhoneNumberChange = async (e) => {
        handleChange(e);

        const currentPhoneNumber = handlePhoneValue(e.target.value);
        const phoneNumberValid = /^\+(?:[0-9]●?){10,15}[0-9]$/g.test(currentPhoneNumber);

        if (phoneNumberValid && currentPhoneNumber) {            
            try {
                const res = await getPhoneType(currentPhoneNumber);
                if (res && res?.status !== 404) {
                    if (contactPhone !== currentPhoneNumber) { setContactPhone (currentPhoneNumber); } 
                    
                    setTwilioContext(res?.carrier);
                    // checkPhoneUnique({
                    //     variables: {
                    //         phoneNumber: currentPhoneNumber,
                    //     },
                    // });
                    checkUniquePhoneCombination(currentPhoneNumber);
                } else {
                }
            } catch (error) {
                return [];
            }
        }
    };

    const onTopicSelect = (o: TDropdownOption) => {
        setTopicSelection(o);
    };

    const onSuffixSelect = (o: TDropdownOption) => {
        setSuffixSelection(o);
    };

    const onOutboundNumberSelect = (o: TDropdownOption) => {
        setOutboundNumberSelection(o);
    };

    const onLocaleSelect = (o: TDropdownOption) => {
        setLocaleSelection(o);
    };

    const onCountrySelect = (o: TDropdownOption) => {
        setCountrySelection(o);
    };

    const onAdministrativeAreaSelect = (o: TDropdownOption) => {
        setAdministrativeAreaSelection(o);
    };

    const optionClassNames = {
        optionHeading: "c-first-login__dropdown-option-heading",
    };

    useEffect(() => {
        if(values.phoneNumber) {
            checkUniquePhoneCombination(values.phoneNumber);
        }
    }, [outboundNumberSelection, values.phoneNumber]);

    return (
        <div>
            <div className="c-add-a-contact" style={componentStyles}>
                <FormHeader name={"ADD_CONTACT"} title={"Add Contact"} onClose={onClose} />
                <Scrollbars hideTracksWhenNotNeeded={true}>
                    <FormBody>
                        {isUsEnv &&
                            <>
                                <div className="c-text-input__label" id={"OUTBOUND_NUMBER"}>
                                    {"Outbound Number to Use"}
                                </div>
                                <SimpleDropdown
                                    isMobileView={isMobile}
                                    multiple={false}
                                    className="c-first-login__dropdown"
                                    dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                                    heading={(outboundNumberSelection && outboundNumberSelection?.label) ? t(outboundNumberSelection?.label) : numbersOptions[0]?.label}
                                    onSelect={onOutboundNumberSelect}
                                    optionClassNames={optionClassNames}
                                    options={numbersOptions}
                                    search={false}
                                    selectedOption={selectedOption}
                                />
                                <div className="c-add-a-contact__space"></div>
                            </>
                        }
                        <TextInput
                            inputId={"ADD_CONTACT_PHONE"}
                            required
                            name="phoneNumber"
                            label={t("Contact Phone Number")}
                            value={handlePhoneValue(values?.phoneNumber)}
                            onChange={(e) => onPhoneNumberChange(e)}
                            error={values?.phoneNumber?.length > 0 && errors?.phoneNumber ? errors?.phoneNumber : undefined}
                            customMessage={((twilioContext?.type || phoneStatus?.isDuplicate) && !errors?.phoneNumber) ? renderPhoneMessage() : null}
                        />
                        <div className="c-add-a-contact__space"></div>

                        {!phoneStatus?.isDuplicate && <>
                        <TextInput
                            inputId={"ADD_CONTACT_FIRSTNAME"}
                            disabled={phoneStatus?.isDuplicate}
                            required
                            name="firstName"
                            label={t("First Name")}
                            value={values?.firstName}
                            onChange={handleChange}
                            error={values?.firstName && errors?.firstName ? errors?.firstName : undefined}
                        />
                        <div className="c-add-a-contact__space"></div>
                        </>}

                        {values?.phoneNumber && values?.firstName && !errors?.phoneNumber && !errors?.firstName &&
                            <>
                                {advancedOption && isUsEnv &&
                                    <>
                                        <TextInput
                                            inputId={"ADD_CONTACT_MIDDLE_NAME"}
                                            disabled={phoneStatus?.isDuplicate}
                                            name="middleName"
                                            label={t("Middle Name")}
                                            value={values.middleName}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            error={errors?.middleName ? errors?.middleName : undefined}
                                        />
                                        <div className="c-add-a-contact__space"></div>
                                    </>
                                }

                                <TextInput
                                    inputId={"ADD_CONTACT_LASTNAME"}
                                    disabled={phoneStatus?.isDuplicate}
                                    name="lastName"
                                    label={t("Last Name")}
                                    value={values?.lastName}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    error={errors?.lastName ? errors?.lastName : undefined}
                                />
                                <div className="c-add-a-contact__space"></div>

                                {advancedOption && isUsEnv &&
                                    <>
                                        <div className="c-text-input__label" id={"OUTBOUND_NUMBER"}>
                                            {"Suffix"}
                                        </div>
                                        <SimpleDropdown
                                            isMobileView={isMobile}
                                            multiple={false}
                                            className="c-first-login__dropdown"
                                            dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                                            heading={suffixSelection ? t(suffixSelection.label) : t("")}
                                            onSelect={onSuffixSelect}
                                            optionClassNames={optionClassNames}
                                            options={suffixOptions}
                                            search={false}
                                            selectedOption={selectedOption}
                                        />
                                        <div className="c-add-a-contact__space"></div>
                                    </>
                                }

                                {advancedOption && isUsEnv &&
                                    <>
                                        <TextInput
                                            inputId={"ADD_CONTACT_BUSINESS_NAME"}
                                            disabled={phoneStatus?.isDuplicate}
                                            name="businessName"
                                            label={t("Business Name")}
                                            value={values?.businessName}
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            error={errors?.businessName ? errors?.businessName : undefined}
                                        />
                                        <div className="c-add-a-contact__space"></div>
                                    </>
                                }
                                

                                {!errors?.firstName && !phoneStatus?.isDuplicate && (values?.firstName)?.length > 0 && values?.phoneNumber?.length > 0 && (<>
                                    {topicSelection && topicSelection?.label && <div className="c-text-input__label" id={"TOPIC"}>
                                        {"Topic to Assign"}
                                    </div>}
                                    {topicOptions && <SimpleDropdown
                                        isMobileView={isMobile}
                                        multiple={false}
                                        className="c-first-login__dropdown"
                                        dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                                        heading={topicSelection ? t(topicSelection?.label) : t("Select Topic*")}
                                        onSelect={onTopicSelect}
                                        optionClassNames={optionClassNames}
                                        options={topicOptions}
                                        search={false}
                                        selectedOption={selectedOption}
                                    />}
                                    <div className="c-add-a-contact__space"></div>
                                </>)
                                }

                                
                            </>
                        }
                        {advancedOption && isUsEnv &&
                            <>
                                <TextInput
                                    inputId={"ADD_CONTACT_EXTERNAL_ID"}
                                    disabled={phoneStatus?.isDuplicate}
                                    name="externalId"
                                    label={t("Identifier")}
                                    value={values?.externalId}
                                    onChange={handleChange}
                                    error={(!phoneStatus?.isDuplicate && twilioContext?.type && !errors?.phoneNumber && errors?.externalId) ? errors?.externalId : undefined}
                                />
                                <small>Example: External ID, Contact ID, Account ID</small>
                                <div className="c-add-a-contact__space"></div>

                                <div className="c-text-input__label" id={"PREFERRED_LANGUAGE"}>
                                    {"Preferred Language"}
                                </div>
                                <SimpleDropdown
                                    isMobileView={isMobile}
                                    multiple={false}
                                    className="c-first-login__dropdown"
                                    dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                                    heading={localeSelection ? localeSelection?.heading : null}
                                    onSelect={onLocaleSelect}
                                    optionClassNames={optionClassNames}
                                    options={localeOptions}
                                    search={false}
                                    selectedOption={selectedOption}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_STREET_NUMBER"}
                                    name="streetNumber"
                                    type="number"
                                    label={t("Street Number")}
                                    value={values?.streetNumber}
                                    onChange={handleChange}
                                    error={errors?.streetNumber ? errors?.streetNumber : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_STREET_NAME"}
                                    name="streetName"
                                    label={t("Street Name")}
                                    value={values?.streetName}
                                    onChange={handleChange}
                                    error={errors?.streetName ? errors?.streetName : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_STREET_SUFFIX"}
                                    name="streetSuffix"
                                    label={t("Street Suffix")}
                                    value={values?.streetSuffix}
                                    onChange={handleChange}
                                    error={errors?.streetSuffix ? errors?.streetSuffix : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_STREET_DIRECTION"}
                                    name="streetDirection"
                                    label={t("Street Direction")}
                                    value={values?.streetDirection}
                                    onChange={handleChange}
                                    error={errors?.streetDirection ? errors?.streetDirection : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_UNIT_NUMBER"}
                                    name="unitNumber"
                                    type="number"
                                    label={t("Unit Number")}
                                    value={values?.unitNumber}
                                    onChange={handleChange}
                                    error={errors?.unitNumber ? errors?.unitNumber : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_UNIT_TYPE"}
                                    name="unitType"
                                    label={t("Unit Type")}
                                    value={values?.unitType}
                                    onChange={handleChange}
                                    error={errors?.unitType ? errors?.unitType : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_PO_BOX"}
                                    name="postalCode"
                                    label={t("PO Box")}
                                    value={values?.poBox}
                                    onChange={handleChange}
                                    error={errors?.poBox ? errors?.poBox : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_RR_ROUTE"}
                                    name="postalCode"
                                    label={t("Rural Route #")}
                                    value={values?.ruralRoute}
                                    onChange={handleChange}
                                    error={errors?.ruralRoute ? errors?.ruralRoute : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_LOCALITY"}
                                    name="locality"
                                    label={t("City or Locality")}
                                    value={values?.locality}
                                    onChange={handleChange}
                                    error={errors?.locality ? errors?.locality : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <TextInput
                                    inputId={"ADD_CONTACT_POSTAL_CODE"}
                                    name="postalCode"
                                    label={t("Postal or ZIP Code")}
                                    value={values?.postalCode}
                                    onChange={handleChange}
                                    error={errors?.postalCode ? errors?.postalCode : null}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <div className="c-text-input__label" id={"PREFERRED_LANGUAGE"}>
                                    {"Country"}
                                </div>
                                <SimpleDropdown
                                    isMobileView={isMobile}
                                    multiple={false}
                                    className="c-first-login__dropdown"
                                    dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                                    heading={countrySelection ? countrySelection?.heading : null}
                                    onSelect={onCountrySelect}
                                    optionClassNames={optionClassNames}
                                    options={countryOptions}
                                    search={false}
                                    selectedOption={selectedOption}
                                />
                                <div className="c-add-a-contact__space"></div>

                                <div className="c-text-input__label" id={"PREFERRED_LANGUAGE"}>
                                    {"State or Province"}
                                </div>
                                <SimpleDropdown
                                    isMobileView={isMobile}
                                    multiple={false}
                                    className="c-first-login__dropdown"
                                    dropdownDefaultRenderClassNames={{ header: "c-first-login__dropdown-header" }}
                                    heading={administrativeAreaSelection ? administrativeAreaSelection?.heading : null}
                                    onSelect={onAdministrativeAreaSelect}
                                    optionClassNames={optionClassNames}
                                    options={administrativeAreaOptions}
                                    search={false}
                                    selectedOption={selectedOption}
                                />
                                <div className="c-add-a-contact__space"></div>

                            </>
                        }

                        {!errors?.firstName && !phoneStatus?.isDuplicate && (values?.firstName)?.length > 0 && values?.phoneNumber?.length > 0 && (
                            <>
                                <Checkbox
                                    id={"ADD_CONTACT_CONSENT_CHECKBOX"}
                                    value={{ checked: hasConsent, label: t("This person has given consent to communicate for 30 days or is a customer with a recent transaction.") }}
                                    onChange={(box) => {
                                        setHasConsent(box.checked);
                                    }}
                                />
                                <div className="c-add-a-contact__space"></div>
                            </>
                        )}
                        {!advancedOption && !phoneStatus?.isDuplicate && isUsEnv && 
                            <div className="c-form__fotter-button-center">
                                <Button
                                    id={`SHOW_MORE_SUBMIT_BUTTON`}
                                    className="c-form__footer-button"
                                    size={ButtonSize.SMALL}
                                    border={true}
                                    color={ButtonColor.SECONDARY}
                                    onClick={() => setAdvancedOption(true)}
                                    ariaLabel={"See More Options"}
                                >
                                    {"See More Options"}
                                </Button>
                                <div className="c-add-a-contact__space"></div>
                            </div>
                        } 
                        {advancedOption && isUsEnv && 
                            <div className="c-form__fotter-button-center">
                                <Button
                                    id={`SHOW_MORE_SUBMIT_BUTTON`}
                                    className="c-form__footer-button"
                                    size={ButtonSize.SMALL}
                                    border={true}
                                    color={ButtonColor.SECONDARY}
                                    onClick={() => setAdvancedOption(false)}
                                    ariaLabel={"Hide Options"}
                                >
                                    {"Hide Options"}
                                </Button>
                                <div className="c-add-a-contact__space"></div>
                            </div>
                        } 

                        {/*twilioContext?.type === "mobile" && !errors.firstname && (values.firstname).length > 0 && !phoneStatus?.isDuplicate && (<Checkbox 
                        id={"SEND_CONSENT_CHECKBOX"}
                        value={{checked: needsOptIn, label: t("Request double-opt-in. They must reply with the word YES.")}}
                        onChange={(box) => {
                            setNeedsOptIn(box.checked);
                        }}
                    />)
                    isValid || !dirty
                    */}


                    </FormBody>
                    <FormFooter
                        name={"ADD_CONTACT"}
                        onCancel={handleCancel}
                        submitTitle={t("Create Contact")}
                        submitDisabled={!isValid || !dirty || !topicSelection || (!hasConsent) || phoneStatus?.isDuplicate}
                        onSubmit={handleSubmitForm} />
                    <div className="c-add-a-contact__space"></div>
                    <div className="c-add-a-contact__space"></div>
                </Scrollbars>

            </div>
        </div>
    );
};
export default AddAContact;